import React, { useEffect, useState, useRef } from "react";
import { Birth, tzOffsetHours } from "../../utils/calculatorApi";
import { searchCities } from "./cities";
import "./BirthInput.css";

interface Suggestion {
  formatted: string;
  lat: number;
  lon: number;
  timezone?: { name?: string };
}

/* Date, time and place, which is everything the chart engine needs. Place
   autocompletes through Geoapify, the same provider the profile form already
   uses, so there is one geocoder in the app rather than two. */

/* Meta's in-app browser (Instagram, Facebook, Threads) and iOS's specialised
   time picker do not get along: tapping an <input type="time"> can dismiss the
   webview outright, which sends the visitor back to the feed and loses the
   form. Reported from a live Instagram ad, where every visitor arrives inside
   that browser.
 *
 * Two <select>s instead. Selects use the ordinary iOS picker sheet, which is
 * one of the oldest and best-supported controls in any webview, and they carry
 * the same precision: an exact hour and an exact minute.
 */
/* Checked at render, not once at module load: a module-level constant is
   evaluated before the component ever mounts (and before a prerendered page
   hydrates), and it cannot be exercised by a test without loading a second copy
   of React. Cost is one regex per render. */
export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /(Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|Twitter|Snapchat)/i.test(
    navigator.userAgent || "",
  );
}

/* 1-12, with AM/PM as its own control. A single 24-entry list labelled
   "1 AM ... 11 PM" made the visitor scroll a long way to find an evening hour
   and read the meridiem off every single option. */
const HOUR12_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const h = i + 1;
  return { value: String(h), label: String(h) };
});
const MERIDIEM_OPTIONS = ["AM", "PM"] as const;

/** "07" + "PM" -> "19". Midnight is 12 AM -> 00, noon is 12 PM -> 12. */
const to24 = (h12: string, mer: string): string => {
  const n = Number(h12);
  if (!n) return "";
  const h = mer === "PM" ? (n === 12 ? 12 : n + 12) : n === 12 ? 0 : n;
  return String(h).padStart(2, "0");
};
/** "19" -> { h12: "7", mer: "PM" } */
const from24 = (hh: string): { h12: string; mer: string } => {
  const n = Number(hh);
  if (Number.isNaN(n)) return { h12: "", mer: "AM" };
  return {
    h12: String(n % 12 === 0 ? 12 : n % 12),
    mer: n < 12 ? "AM" : "PM",
  };
};
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, m) =>
  String(m).padStart(2, "0"),
);

/** The conventional stand-in when a birth time is genuinely unknown. */
const NOON = "12:00";

/** Same "HH:MM" contract as the native control, so nothing downstream changes. */
const TimeOfBirth: React.FC<{
  value: string;
  onChange: (next: string) => void;
  /** Force the two-select control even outside an in-app browser. The native
      picker is a known drop-off on a long two-person form (both competitors on
      this query built their own for the same reason), so the kundli matching
      page opts in for everyone. Default off, so no other calculator changes. */
  alwaysSplit?: boolean;
  /** Greyed and unusable. The value is still published (noon), but showing
      "12 / 00 / PM" in live-looking controls read as a time the visitor had
      chosen, which is exactly what ticking the box says they have not. */
  disabled?: boolean;
}> = ({ value, onChange, alwaysSplit, disabled }) => {
  /* The half-picked state lives HERE, not in the composed value.
     Deriving the selects from `value` looked tidier and made the control
     impossible to use: choosing an hour emitted "" (no minute yet), which fed
     straight back in and blanked the select. Neither half could ever be set. */
  const [h12, setH12] = useState(() => (value ? from24(value.split(":")[0]).h12 : ""));
  const [mer, setMer] = useState(() => (value ? from24(value.split(":")[0]).mer : "AM"));
  const [mm, setMm] = useState(() => (value ? value.split(":")[1] : ""));

  useEffect(() => {
    if (!value) return;
    const [hh, m] = value.split(":");
    const p = from24(hh);
    setH12(p.h12);
    setMer(p.mer);
    setMm(m);
  }, [value]);

  if (!alwaysSplit && !isInAppBrowser()) {
    return (
      <input
        type="time"
        value={disabled ? "" : value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  /* Only a COMPLETE time is published: a half-set one would have the chart
     computed from a default the visitor never chose, and an hour out moves the
     ascendant. */
  const emit = (nextH: string, nextM: string, nextMer: string) => {
    setH12(nextH);
    setMm(nextM);
    setMer(nextMer);
    onChange(nextH && nextM ? `${to24(nextH, nextMer)}:${nextM}` : "");
  };

  /* Hour and minute are PICKED, not typed. Typing them meant remembering to
     pad, getting corrected on blur, and a caret to manage in two boxes; a
     dropdown is one tap and shows the valid range. AM/PM stays a switch,
     because two options do not deserve a menu. */
  const commit = (nextH: string, nextM: string, nextMer: string) => {
    setH12(nextH);
    setMm(nextM);
    setMer(nextMer);
    /* Only a COMPLETE time is published: a half-set one would have the chart
       computed from a default the visitor never chose, and an hour out moves
       the ascendant. */
    onChange(nextH && nextM ? `${to24(nextH, nextMer)}:${nextM}` : "");
  };

  return (
    <div
      className={"birthinput-timepair" + (disabled ? " is-disabled" : "")}
    >
      <select
        aria-label="Hour of birth"
        disabled={disabled}
        value={disabled ? "" : h12}
        onChange={(e) => commit(e.target.value, mm, mer)}
      >
        <option value="">Hour</option>
        {HOUR12_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Minute of birth"
        disabled={disabled}
        value={disabled ? "" : mm}
        onChange={(e) => commit(h12, e.target.value, mer)}
      >
        <option value="">Min</option>
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <div
        className="birthinput-mer"
        role="group"
        aria-label="AM or PM"
        data-on={mer}
      >
        <span className="birthinput-mer__thumb" aria-hidden="true" />
        {MERIDIEM_OPTIONS.map((m) => (
          <button
            key={m}
            type="button"
            className={"birthinput-mer__btn" + (mer === m ? " is-on" : "")}
            aria-pressed={mer === m}
            disabled={disabled}
            onClick={() => commit(h12, mm, m)}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
};

const BirthInput: React.FC<{
  /**
   * `timezone` is the IANA zone name ("Asia/Kolkata"), passed as a THIRD
   * argument so existing callers keep working untouched. Birth.tz is only the
   * numeric offset, which is all the calculators need, but turning a match into
   * real profiles needs the zone name too (user_birth_details.birth_timezone),
   * and it is lost by the time tzOffsetHours() has run.
   */
  onSubmit: (b: Birth, placeLabel: string, timezone?: string) => void;
  busy?: boolean;
  cta: string;
  busyLabel?: string;
  /** Commit as soon as date, time and place are all present, and hide the
      button. For flows that ask for two charts, where a Confirm per side is a
      step the visitor should not have to take. Off by default: every existing
      caller keeps its button. */
  autoCommit?: boolean;
  /** Change this to make autoCommit re-evaluate. Needed when a value the parent
      owns (the name) is part of what makes the commit valid. */
  recommitKey?: string | number;
  /** Use the two-select time control for everyone, not only in-app browsers. */
  alwaysSplitTime?: boolean;
  /** Show the "I don't know the exact birth time" escape hatch. */
  offerUnknownTime?: boolean;
}> = ({
  onSubmit,
  busy,
  cta,
  busyLabel,
  autoCommit,
  recommitKey,
  alwaysSplitTime,
  offerUnknownTime,
}) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [unknownTime, setUnknownTime] = useState(false);
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState<Suggestion | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (place || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      const key = process.env.REACT_APP_GEOAPIFY_API_KEY;
      fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          query
        )}&format=json&limit=5&apiKey=${key}`
      )
        .then((r) => r.json())
        .then((d) => {
          /* Geoapify answers as {results} with format=json and as GeoJSON
             {features} without it. Read both so a change at their end, or a
             stray param, cannot silently empty the dropdown. */
          if (d?.results?.length) return setSuggestions(d.results);
          if (d?.features?.length)
            return setSuggestions(
              d.features.map((f: any) => ({
                formatted: f.properties?.formatted,
                lat: f.properties?.lat,
                lon: f.properties?.lon,
                timezone: f.properties?.timezone,
              }))
            );
          setSuggestions(searchCities(query));
        })
        .catch(() => setSuggestions(searchCities(query)));
    }, 350);
    return () => clearTimeout(t);
  }, [query, place]);

  /* Auto-commit fires on the value, not on every render, so re-renders from a
     sibling panel cannot re-submit the same details. */
  const lastCommitted = useRef<string>("");
  /* Clearing the guard lets the same birth details commit again. Without this,
     filling date/time/place BEFORE the name commits once with an empty name,
     the parent rejects it, and then typing the name changes nothing: the
     signature is unchanged so nothing re-fires and the CTA never turns on. */
  useEffect(() => {
    lastCommitted.current = "";
  }, [recommitKey]);
  useEffect(() => {
    if (!autoCommit || busy) return;
    if (!date || !time || !place) return;
    const sig = `${date}|${time}|${place.lat}|${place.lon}`;
    if (lastCommitted.current === sig) return;
    lastCommitted.current = sig;
    setError("");
    onSubmit(
      {
        date,
        time,
        lat: place.lat,
        lon: place.lon,
        tz: tzOffsetHours(place.timezone?.name || "Asia/Kolkata", date),
      },
      place.formatted,
      place.timezone?.name,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCommit, busy, date, time, place, recommitKey]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!date || !time) {
      setError("Please give both a date and a time of birth.");
      return;
    }
    if (!place) {
      setError("Pick your birth place from the list so we get the coordinates.");
      return;
    }
    setError("");
    onSubmit(
      {
        date,
        time,
        lat: place.lat,
        lon: place.lon,
        tz: tzOffsetHours(place.timezone?.name || "Asia/Kolkata", date),
      },
      place.formatted,
      place.timezone?.name
    );
  };

  return (
    <form className="birthinput" onSubmit={submit}>
      <div className="birthinput-row">
        <label className="birthinput-field">
          <span>Date of birth</span>
          {/* Tapping anywhere in the field opens the picker, not just the
              small calendar glyph at the right end. Chrome and Edge only open
              on that glyph by default, so most of the control looked tappable
              and did nothing.

              NOT inside Meta's in-app browser. <input type="time"> could
              dismiss that webview outright and lose the whole form, which is
              why the time control is three of our own controls in there;
              type="date" is the same class of native picker and every visitor
              from an Instagram ad arrives in that browser, so calling
              showPicker() there is a risk taken for no gain. Ordinary browsers
              are unaffected. */}
          <input
            type="date"
            className="birthinput-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            onClick={(e) => {
              if (isInAppBrowser()) return;
              const el = e.currentTarget as HTMLInputElement & {
                showPicker?: () => void;
              };
              try {
                el.showPicker?.();
              } catch {
                /* Not user-activated, or unsupported: leave the native
                   behaviour alone rather than breaking the field. */
              }
            }}
          />
        </label>
        <label className="birthinput-field">
          <span>Time of birth</span>
          <TimeOfBirth
            value={time}
            onChange={(t) => {
              setTime(t);
              if (t !== NOON) setUnknownTime(false);
            }}
            alwaysSplit={alwaysSplitTime}
            disabled={unknownTime}
          />
        </label>
      </div>

      {/* The single biggest reason a visitor abandons a kundli form is not
          knowing their birth time. Refusing to proceed loses them entirely;
          12:00 noon is the standard convention for an unknown time and at
          least gets the Moon-based work right, which is most of a gun milan.
          Opt-in, so calculators that genuinely need an exact time (the
          ascendant-dependent ones) are unaffected. */}
      {offerUnknownTime ? (
        <label className="birthinput-unknown">
          <input
            type="checkbox"
            checked={unknownTime}
            onChange={(e) => {
              setUnknownTime(e.target.checked);
              setTime(e.target.checked ? NOON : "");
            }}
          />
          <span>I don't know my exact birth time</span>
        </label>
      ) : null}
      {/* Standing note, not something that appears when the box is ticked:
          the point is to tell people the time is optional BEFORE they give up
          on the form, which is too late once they have already ticked it. */}
      {offerUnknownTime ? (
        <p className="birthinput-timenote">
          <span className="birthinput-timenote__icon" aria-hidden="true">i</span>
          <span>
            No exact time? We can still build the chart. An exact one sharpens
            your lagna and houses.
          </span>
        </p>
      ) : null}


      <label className="birthinput-field birthinput-place">
        <span>Place of birth</span>
        <input
          type="text"
          value={query}
          placeholder="Start typing a city"
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setPlace(null);
          }}
        />
        {suggestions.length > 0 ? (
          <ul className="birthinput-suggestions">
            {suggestions.map((s) => (
              <li key={`${s.lat}-${s.lon}-${s.formatted}`}>
                <button
                  type="button"
                  onClick={() => {
                    setPlace(s);
                    setQuery(s.formatted);
                    setSuggestions([]);
                  }}
                >
                  {s.formatted}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </label>

      {/* The checkbox above already offers the way out, in a form the visitor
          can act on, so this paragraph would just repeat it, twice, once per
          column. Kept for every other caller, which has no checkbox. */}
      {offerUnknownTime ? null : (
        <p className="birthinput-note">
          Birth time matters. An hour out can move the rising sign and the
          houses, which changes the answer. If you are unsure, use the closest
          time you know and treat the result as approximate.
        </p>
      )}
      {autoCommit ? null : (

      <button type="submit" className="birthinput-submit" disabled={busy}>
        {busy ? busyLabel || "Reading your chart…" : cta}
      </button>
      )}
      {error ? <p className="birthinput-error">{error}</p> : null}
    </form>
  );
};

export default BirthInput;
